import { ChangeEvent, FC, KeyboardEvent, useEffect, useState } from "react";
import {
  Chip,
  StyledIcon,
  ErrorMessage,
  Container,
  TextFieldContainer,
} from "./MultiValueTextField.ui";
import { BaseComponentProps } from "../types";
import { TextField } from "../TextField";

export enum MultiValueTextFieldTestIds {
  Component = "multi-value-text-field",
}

export enum ValidationType {
  Email = "email",
}

const regexByValidationType: {
  [key in ValidationType]: RegExp;
} = {
  [ValidationType.Email]: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
};

export type Chip = {
  label: string;
  value: string;
};

type MultiValueTextFieldProps = Omit<BaseComponentProps<"input">, "value"> & {
  id?: string;
  className?: string;
  testId?: string;
  placeholder?: string;
  errorMessage?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValidate?: (isValid: boolean) => void;
  value?: Chip[];
  onAddChip?: (chip: Chip) => void;
  onCloseChip?: (chip: Chip) => void;
  validation?: ValidationType;
  customValidation?: RegExp;
};

export const MultiValueTextField: FC<MultiValueTextFieldProps> = ({
  id,
  className,
  testId,
  placeholder,
  value: controlledValue,
  onAddChip,
  onCloseChip,
  onChange,
  onValidate,
  errorMessage = "Invalid input.",
  validation,
  customValidation,
  ...inputProps
}) => {
  const [currentText, setCurrentText] = useState("");
  const [value, setValue] = useState(controlledValue || []);
  const [isValid, setIsValid] = useState(true);
  const [invalidChips, setInvalidChips] = useState<Chip[]>([]);

  const isControlledComponent = controlledValue !== undefined;

  useEffect(() => {
    setValue(controlledValue || []);
  }, [controlledValue]);

  useEffect(() => {
    handleValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const isValidInputText = (text: string): boolean => {
    let isValidInputText = true;

    if (validation) {
      const myRegex: RegExp = regexByValidationType[validation];
      isValidInputText = isValidInputText && myRegex.test(text);
    }
    if (isValidInputText && customValidation) {
      isValidInputText = isValidInputText && customValidation.test(text);
    }

    return isValidInputText;
  };

  const handleValidation = () => {
    const invalidChips = value.filter((chip) => !isValidInputText(chip.value));
    const isValidValue = invalidChips?.length <= 0;

    setIsValid(isValidValue);
    setInvalidChips(invalidChips);
    onValidate?.(isValidValue);
  };

  const createNewChip = (chip: Chip) => {
    if (!isControlledComponent) {
      setValue([...value, chip]);
    }
    onAddChip?.(chip);
    setCurrentText("");
  };

  /**
   * unlike with handleOnKeyDown, we're calling createNewChip
   * everytime the blur event is called, regardless of if the
   * component is being used in a controlled manner. we should
   * confirm later if handleOnKeyDown should follow the same
   * behavior or not
   */
  const handleOnBlur = () => {
    if (currentText) {
      createNewChip({ label: currentText, value: currentText });
    }
  };

  const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      currentText &&
      (event.code === "Enter" ||
        event.code === "Space" ||
        event.code === "Comma")
    ) {
      const newChip: Chip = {
        label: currentText,
        value: currentText,
      };
      createNewChip(newChip);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ",") {
      setCurrentText(event.target.value.trim());
      onChange?.(event);
    }
  };

  const handleCloseChip = (chipToDelete: Chip) => {
    if (!isControlledComponent) {
      setValue(value.filter((chip) => chip.value !== chipToDelete.value));
    }

    onCloseChip?.(chipToDelete);
  };

  return (
    <Container
      id={id}
      className={className}
      data-testid={testId ?? MultiValueTextFieldTestIds.Component}
    >
      <TextFieldContainer className={isValid ? "" : "danger"}>
        {value.map((chip, i) => (
          <Chip
            key={`${chip.value}-${i}`}
            className={
              invalidChips.find(
                (invalidChip) => invalidChip.value === chip.value
              )
                ? "danger"
                : ""
            }
          >
            {chip.label}
            <StyledIcon
              iconName="x-close"
              iconSize="small"
              plain
              onClick={(): void => handleCloseChip(chip)}
            />
          </Chip>
        ))}
        <TextField
          value={currentText}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          placeholder={placeholder}
          {...inputProps}
        />
      </TextFieldContainer>
      {!isValid && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Container>
  );
};
